import { makeAutoObservable, runInAction } from 'mobx';
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import type { 
  AdminBookingResponseDto,
  AdminBookingServiceDto,
  AdminBookingPostDto,
  UpdateAdminBookingDto,
} from '@pro-sto/api-client';
import type { AdminDetailedBooking } from '@/types/bookings';
import { getBookingPostUuid } from '@/features/bookings/getBookingPostUuid';
import {
  adminBookingsGetList,
  adminBookingsGetOne,
  adminBookingsUpdate,
  adminBookingsUpdateStatus,
  adminBookingsGetPending,
  ApiError,
} from '@pro-sto/api-client';
import { toastStore } from './ToastStore';
import { postsStore } from './PostsStore';

const getApiErrorMessage = (error: ApiError): string => {
  if (error.body && typeof error.body === 'object' && error.body !== null) {
    const { message } = error.body as { message?: unknown };
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
    if (Array.isArray(message)) {
      const parts = message.filter(
        (item): item is string => typeof item === 'string' && item.trim().length > 0,
      );
      if (parts.length > 0) {
        return parts.join('. ');
      }
    }
  }

  return error.message;
};

const resolveUpdatedPost = (
  postUuid: string | undefined,
  currentPost: AdminBookingPostDto | null,
): AdminBookingPostDto | null => {
  if (!postUuid) {
    return currentPost;
  }

  const orderNumber =
    postsStore.posts.find((post) => post.uuid === postUuid)?.orderNumber ??
    (currentPost?.uuid === postUuid ? currentPost.orderNumber : undefined) ??
    0;

  return { uuid: postUuid, orderNumber };
};

// Тип статуса бронирования
export type BookingStatus = 'pending_confirmation' | 'confirmed' | 'completed' | 'cancelled';

/**
 * Store для управления бронированиями (заказами)
 * Управляет загрузкой, фильтрацией, редактированием и изменением статусов заказов
 */
export class BookingsStore {
  // Локальный fallback, если API ещё не вернул имя (оптимистичный UI до refresh)
  private clientDisplayNames = new Map<string, string>();

  private mergeClientDisplayNames = (
    bookings: AdminBookingResponseDto[],
  ): AdminBookingResponseDto[] => {
    if (this.clientDisplayNames.size === 0) {
      return bookings;
    }

    return bookings.map((booking) => {
      if (booking.created_by_source === 'client_app') {
        return booking;
      }

      const clientUuid = booking.client?.uuid ?? booking.client_uuid;
      const override = this.clientDisplayNames.get(clientUuid);
      if (!override) {
        return booking;
      }

      return {
        ...booking,
        client: {
          ...booking.client,
          name: override,
        },
      };
    });
  };

  /**
   * Запоминает имя клиента для отображения в карточках после refresh.
   * Для записей из админки подставляет имя во все карточки клиента (и в БД оно уходит через find-or-create).
   * Записи из приложения не трогаем.
   */
  setClientDisplayName = (clientUuid: string, name: string): void => {
    const trimmed = name.trim();
    if (!trimmed || !clientUuid) {
      return;
    }

    runInAction(() => {
      this.clientDisplayNames.set(clientUuid, trimmed);
      this.bookings = this.mergeClientDisplayNames(this.bookings);
      this.pendingBookings = this.mergeClientDisplayNames(this.pendingBookings);
    });
  };
  
  // Список бронирований
  bookings: AdminBookingResponseDto[] = [];
  
  // Список бронирований ожидающих подтверждения
  pendingBookings: AdminBookingResponseDto[] = [];
  
  // Детальная информация о выбранном бронировании
  selectedBooking: AdminDetailedBooking | null = null;
  
  // Состояние загрузки
  isLoading = false;
  isLoadingDetails = false;
  isLoadingPending = false;
  
  // Ошибки
  error: string | null = null;

  // Фильтры
  dateFrom: Date = startOfWeek(new Date(), { weekStartsOn: 1 }); // начало текущей недели (понедельник)
  dateTo: Date = endOfWeek(new Date(), { weekStartsOn: 1 }); // конец текущей недели (воскресенье)
  selectedStatuses: BookingStatus[] = []; // пустой массив = все статусы

  // UUID текущего сервисного центра (из профиля админа)
  serviceCenterUuid: string | null = null;

  // Пагинация
  total = 0;
  limit = 100;
  offset = 0;

  // Кэш для предотвращения ненужных загрузок и скелетонов
  private bookingsCache = new Map<string, AdminBookingResponseDto[]>();
  private readonly MAX_CACHE_SIZE = 10; // Максимум 10 периодов в кэше
  private isInitialLoad = true;
  private fetchRequestId = 0; // Счетчик для предотвращения race condition

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Установка UUID сервисного центра
   */
  setServiceCenterUuid(uuid: string) {
    if (this.serviceCenterUuid !== uuid) {
      this.serviceCenterUuid = uuid;
      this.bookingsCache.clear(); // Очищаем кэш при смене центра
      this.bookings = [];
      this.pendingBookings = [];
    }
  }

  /**
   * Установка диапазона дат для фильтрации
   */
  setDateRange(from: Date, to: Date) {
    this.dateFrom = from;
    this.dateTo = to;
  }

  /**
   * Установка фильтра по статусам
   */
  setSelectedStatuses(statuses: BookingStatus[]) {
    this.selectedStatuses = statuses;
  }

  /**
   * Генерирует ключ кэша для текущего запроса
   */
  private getCacheKey(): string {
    const dateFrom = format(this.dateFrom, 'yyyy-MM-dd');
    const dateTo = format(this.dateTo, 'yyyy-MM-dd');
    const statuses = this.selectedStatuses.length > 0 ? this.selectedStatuses.slice().sort().join(',') : 'all';
    return `${this.serviceCenterUuid}_${dateFrom}_${dateTo}_${statuses}`;
  }

  /**
   * Проверяет, есть ли данные в кэше для текущего диапазона дат
   */
  hasCachedData(): boolean {
    const cacheKey = this.getCacheKey();
    return this.bookingsCache.has(cacheKey);
  }

  /**
   * Устанавливает значение в кэш с учетом лимита размера (LRU)
   */
  private setCacheValue(key: string, value: AdminBookingResponseDto[]): void {
    // Если кэш переполнен, удаляем самую старую запись (первую)
    if (this.bookingsCache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.bookingsCache.keys().next().value;
      if (firstKey) {
        this.bookingsCache.delete(firstKey);
      }
    }
    this.bookingsCache.set(key, value);
  }

  /**
   * Создает ключ кэша для указанных дат
   */
  private getCacheKeyForDates(dateFrom: Date, dateTo: Date, statuses: BookingStatus[]): string {
    const from = dateFrom.toISOString().split('T')[0];
    const to = dateTo.toISOString().split('T')[0];
    const statusesStr = statuses.sort().join(',');
    return `${this.serviceCenterUuid}_${from}_${to}_${statusesStr}`;
  }

  /**
   * Предзагружает данные для соседних периодов (предыдущего и следующего)
   * Это позволяет избежать задержек при переключении между периодами
   */
  async prefetchAdjacentPeriods(): Promise<void> {
    if (!this.dateFrom || !this.dateTo || !this.serviceCenterUuid) return;

    const currentDateFrom = new Date(this.dateFrom);
    const currentDateTo = new Date(this.dateTo);
    
    // Вычисляем длительность текущего периода в днях
    const periodDays = Math.ceil((currentDateTo.getTime() - currentDateFrom.getTime()) / (1000 * 60 * 60 * 24)) + 1;

    try {
      // Предзагружаем предыдущий период (если еще не в кэше)
      const prevDateFrom = new Date(currentDateFrom);
      prevDateFrom.setDate(prevDateFrom.getDate() - periodDays);
      const prevDateTo = new Date(currentDateTo);
      prevDateTo.setDate(prevDateTo.getDate() - periodDays);
      
      const prevCacheKey = this.getCacheKeyForDates(prevDateFrom, prevDateTo, this.selectedStatuses);
      if (!this.bookingsCache.has(prevCacheKey)) {
        const prevResponse = await adminBookingsGetList({
          serviceCenterUuid: this.serviceCenterUuid,
          dateFrom: prevDateFrom.toISOString(),
          dateTo: prevDateTo.toISOString(),
          status: this.selectedStatuses.length > 0 ? this.selectedStatuses as any : undefined,
        });
        
        if (prevResponse.data) {
          this.setCacheValue(prevCacheKey, prevResponse.data);
        }
      }

      // Предзагружаем следующий период (если еще не в кэше)
      const nextDateFrom = new Date(currentDateFrom);
      nextDateFrom.setDate(nextDateFrom.getDate() + periodDays);
      const nextDateTo = new Date(currentDateTo);
      nextDateTo.setDate(nextDateTo.getDate() + periodDays);
      
      const nextCacheKey = this.getCacheKeyForDates(nextDateFrom, nextDateTo, this.selectedStatuses);
      if (!this.bookingsCache.has(nextCacheKey)) {
        const nextResponse = await adminBookingsGetList({
          serviceCenterUuid: this.serviceCenterUuid,
          dateFrom: nextDateFrom.toISOString(),
          dateTo: nextDateTo.toISOString(),
          status: this.selectedStatuses.length > 0 ? this.selectedStatuses as any : undefined,
        });
        
        if (nextResponse.data) {
          this.setCacheValue(nextCacheKey, nextResponse.data);
        }
      }
    } catch (error) {
      console.warn('Failed to prefetch adjacent periods:', error);
    }
  }

  /**   * Загрузка списка бронирований
   * @param silent - если true, загрузка произойдет без показа скелетона (тихое обновление)
   */
  async fetchBookings(silent = false): Promise<void> {
    if (!this.serviceCenterUuid) {
      console.warn('ServiceCenterUuid не установлен');
      return;
    }

    const cacheKey = this.getCacheKey();
    const cachedData = this.bookingsCache.get(cacheKey);

    // Если есть кэшированные данные и это не первая загрузка, используем их сразу
    if (cachedData && !this.isInitialLoad) {
      runInAction(() => {
        this.bookings = this.mergeClientDisplayNames(cachedData);
        this.isLoading = false;
      });
      
      // Для кэшированных данных не делаем дополнительный запрос
      // Данные будут обновлены при следующем явном вызове fetchBookings
      return;
    }

    // Показываем скелетон только при первой загрузке или отсутствии кэша
    if (!silent && this.isInitialLoad) {
      this.isLoading = true;
    }
    
    this.error = null;

    // Увеличиваем счетчик запросов для предотвращения race condition
    const currentRequestId = ++this.fetchRequestId;

    try {
      const response = await adminBookingsGetList({
        serviceCenterUuid: this.serviceCenterUuid,
        dateFrom: this.dateFrom.toISOString(),
        dateTo: this.dateTo.toISOString(),
        status: this.selectedStatuses.length > 0 ? this.selectedStatuses as any : undefined,
        limit: this.limit,
        offset: this.offset,
      });

      // Игнорируем устаревший ответ
      if (currentRequestId !== this.fetchRequestId) {
        return;
      }

      runInAction(() => {
        this.bookings = this.mergeClientDisplayNames(response.data);
        this.total = response.total;
        this.isLoading = false;
        this.isInitialLoad = false;
        
        // Сохраняем данные в кэш с учетом лимита
        this.setCacheValue(cacheKey, response.data);
      });

      // Предзагружаем соседние периоды (только при не-silent загрузке)
      if (!silent) {
        this.prefetchAdjacentPeriods().catch(err => {
          console.warn('Failed to prefetch adjacent periods:', err);
        });
      }
    } catch (error) {
      runInAction(() => {
        this.error = 'Ошибка загрузки бронирований';
        this.isLoading = false;
      });
      console.error('Ошибка при загрузке бронирований:', error);
      toastStore.showError('Не удалось загрузить список заказов');
    }
  }

  /**
   * Тихое обновление данных без показа скелетона
   * Используется после изменения статуса или данных бронирования
   */
  async refreshBookings(): Promise<void> {
    // Инвалидируем текущий кэш, чтобы загрузить свежие данные
    const cacheKey = this.getCacheKey();
    this.bookingsCache.delete(cacheKey);
    await this.fetchBookings(true);
  }

  /**
   * Загрузка бронирований в статусе pending_confirmation
   */
  async fetchPendingBookings(): Promise<void> {
    if (!this.serviceCenterUuid) {
      console.warn('ServiceCenterUuid не установлен');
      return;
    }

    this.isLoadingPending = true;
    this.error = null;

    try {
      const response = await adminBookingsGetPending({
        serviceCenterUuid: this.serviceCenterUuid,
      });

      runInAction(() => {
        this.pendingBookings = this.mergeClientDisplayNames(response.data);
        this.isLoadingPending = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Ошибка загрузки ожидающих бронирований';
        this.isLoadingPending = false;
      });
      console.error('Ошибка при загрузке ожидающих бронирований:', error);
      toastStore.showError('Не удалось загрузить список ожидающих заказов');
    }
  }

  /**
   * Найти бронирование в локальном списке (календарь или pending)
   */
  getBookingFromList(uuid: string): AdminBookingResponseDto | undefined {
    return this.bookings.find((b) => b.uuid === uuid) ?? this.pendingBookings.find((b) => b.uuid === uuid);
  }

  /**
   * Дополняет детали бронирования данными из списка:
   * телефон/имя клиента и пост. Пост из списка подставляем только если
   * getOne не вернул ни post, ни post_uuid — иначе берём свежий post_uuid.
   */
  private enrichDetailWithListClient(detail: AdminDetailedBooking, uuid: string): AdminDetailedBooking {
    const listBooking = this.getBookingFromList(uuid);
    if (!listBooking) return detail;

    const listClient = listBooking.client;
    const detailClient = detail.client;
    const needsPhone = Boolean(listClient?.phone && !detailClient?.phone);
    const needsName = Boolean(listClient?.name && !detailClient?.name);
    const hasFreshPostUuid = Boolean(getBookingPostUuid({ post_uuid: detail.post_uuid }));
    const needsPost = Boolean(listBooking.post && !detail.post && !hasFreshPostUuid);

    if (!needsPhone && !needsName && !needsPost) return detail;

    return {
      ...detail,
      client: listClient
        ? {
            ...(detailClient ?? listClient),
            phone: detailClient?.phone ?? listClient.phone,
            name: detailClient?.name ?? listClient.name,
            uuid: detailClient?.uuid ?? listClient.uuid,
          }
        : detailClient,
      post: hasFreshPostUuid
        ? detail.post
        : (detail.post ?? listBooking.post ?? null),
    };
  }

  /**
   * Загрузка детальной информации о бронировании
   */
  async fetchBookingDetails(uuid: string): Promise<void> {
    // Очищаем предыдущие данные перед загрузкой новых
    // чтобы избежать показа данных предыдущего заказа
    this.selectedBooking = null;
    this.isLoadingDetails = true;
    this.error = null;

    try {
      const booking = await adminBookingsGetOne({ uuid });

      runInAction(() => {
        this.selectedBooking = this.enrichDetailWithListClient(
          booking as unknown as AdminDetailedBooking,
          uuid,
        );
        this.isLoadingDetails = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = 'Ошибка загрузки деталей бронирования';
        this.isLoadingDetails = false;
      });
      console.error('Ошибка при загрузке деталей бронирования:', error);
      toastStore.showError('Не удалось загрузить детали заказа');
    }
  }

  /**
   * Обновление статуса бронирования
   * Использует оптимистичное обновление UI
   */
  async updateBookingStatus(uuid: string, status: BookingStatus): Promise<boolean> {
    // Сохраняем текущее состояние для возможности отката
    const bookingIndex = this.bookings.findIndex((b) => b.uuid === uuid);
    const previousStatus = bookingIndex !== -1 ? this.bookings[bookingIndex].status : null;

    // Оптимистичное обновление UI
    runInAction(() => {
      if (bookingIndex !== -1) {
        this.bookings[bookingIndex].status = status;
      } else {
        // Если заказа нет в основном списке (например, был pending),
        // добавляем его туда из списка pendingBookings
        const pendingBooking = this.pendingBookings.find((b) => b.uuid === uuid);
        if (pendingBooking) {
          this.bookings.push({
            ...pendingBooking,
            status: status,
          });
        }
      }

      // Если это выбранное бронирование, обновляем и его
      if (this.selectedBooking?.uuid === uuid) {
        this.selectedBooking.status = status;
      }

      // Если статус изменился с pending_confirmation на другой, удаляем из pendingBookings
      if (status !== 'pending_confirmation') {
        this.pendingBookings = this.pendingBookings.filter((b) => b.uuid !== uuid);
      }
    });

    try {
      await adminBookingsUpdateStatus({
        uuid,
        requestBody: { status },
      });

      // Обновляем кэш после успешного ответа
      this.invalidateCache();

      toastStore.showSuccess('Статус заказа успешно обновлен');
      
      // Тихо обновляем данные с сервера
      await this.refreshBookings();
      
      return true;
    } catch (error) {
      // При ошибке откатываем изменения
      runInAction(() => {
        if (bookingIndex !== -1 && previousStatus !== null) {
          this.bookings[bookingIndex].status = previousStatus;
        }
        if (this.selectedBooking?.uuid === uuid && previousStatus !== null) {
          this.selectedBooking.status = previousStatus as BookingStatus;
        }
      });

      console.error('Ошибка при обновлении статуса:', error);
      toastStore.showError('Не удалось обновить статус заказа');
      return false;
    }
  }

  /**
   * Обновление данных бронирования
   * Использует оптимистичное обновление UI
   */
  async updateBooking(
    uuid: string,
    data: UpdateAdminBookingDto,
  ): Promise<boolean> {
    try {
      const requestBody: UpdateAdminBookingDto = {};
      if (data.start_time) requestBody.start_time = data.start_time;
      if (data.end_time) requestBody.end_time = data.end_time;
      if (data.post_uuid) requestBody.post_uuid = data.post_uuid;
      if (data.service_uuid) requestBody.service_uuid = data.service_uuid;
      if (data.additionalServiceUuids) requestBody.additionalServiceUuids = data.additionalServiceUuids;
      if (data.client_comment !== undefined) requestBody.client_comment = data.client_comment;

      const updatedBooking = await adminBookingsUpdate({
        uuid,
        requestBody,
      });

      runInAction(() => {
        const bookingIndex = this.bookings.findIndex((b) => b.uuid === uuid);
        if (bookingIndex !== -1) {
          const current = this.bookings[bookingIndex];
          const nextPost = resolveUpdatedPost(data.post_uuid, current.post);

          this.bookings[bookingIndex] = {
            ...current,
            start_time: updatedBooking.start_time ?? data.start_time ?? current.start_time,
            end_time: updatedBooking.end_time ?? data.end_time ?? current.end_time,
            total_cost: updatedBooking.total_cost,
            service: updatedBooking.service as unknown as AdminBookingServiceDto,
            additionalServices: updatedBooking.additionalServices as unknown as AdminBookingServiceDto[] | undefined,
            post: nextPost,
          };
        }

        if (this.selectedBooking?.uuid === uuid) {
          this.selectedBooking = updatedBooking as unknown as AdminDetailedBooking;
        }
      });

      // Обновляем кэш после успешного ответа
      this.invalidateCache();

      toastStore.showSuccess('Заказ успешно обновлен');
      
      // Тихо обновляем данные с сервера
      await this.refreshBookings();
      
      return true;
    } catch (error) {
      console.error('Ошибка при обновлении бронирования:', error);

      if (error instanceof ApiError) {
        if (error.status === 409 || error.status === 400) {
          toastStore.showError(getApiErrorMessage(error));
        } else {
          toastStore.showError('Не удалось обновить заказ');
        }
      } else {
        toastStore.showError('Не удалось обновить заказ');
      }

      return false;
    }
  }

  /**
   * Инвалидация кэша
   * Используется после изменений данных
   */
  private invalidateCache(): void {
    this.bookingsCache.clear();
  }

  /**
   * Добавление нового бронирования в список (после создания)
   * Используется для оптимистичного обновления UI
   */
  addBooking(booking: AdminBookingResponseDto): void {
    runInAction(() => {
      // Проверяем, входит ли бронирование в текущий временной диапазон
      const bookingDate = new Date(booking.start_time);
      if (bookingDate >= this.dateFrom && bookingDate <= this.dateTo) {
        // Проверяем, нет ли уже такого бронирования в списке
        const exists = this.bookings.some(b => b.uuid === booking.uuid);
        if (!exists) {
          this.bookings.push(booking);
          this.total++;
        }
      }
    });
    
    // Инвалидируем кэш после добавления
    this.invalidateCache();
  }

  /**
   * Подставляет имя клиента в запись из админки.
   * Нужно, чтобы карточка календаря сразу показывала имя из формы создания.
   */
  applyClientNameToBooking(uuid: string, name: string): void {
    const trimmed = name.trim();
    if (!trimmed) return;

    const apply = (booking: AdminBookingResponseDto): AdminBookingResponseDto => {
      if (booking.uuid !== uuid) return booking;
      if (booking.created_by_source === 'client_app') return booking;

      const clientUuid = booking.client?.uuid ?? booking.client_uuid;
      if (clientUuid) {
        this.clientDisplayNames.set(clientUuid, trimmed);
      }

      return {
        ...booking,
        client: {
          ...booking.client,
          name: trimmed,
        },
      };
    };

    runInAction(() => {
      this.bookings = this.bookings.map(apply);
      this.pendingBookings = this.pendingBookings.map(apply);
    });
  }

  /**
   * Очистка выбранного бронирования
   */
  clearSelectedBooking() {
    this.selectedBooking = null;
  }

  /**
   * Геттер: группировка бронирований по датам
   * Возвращает Map, где ключ - дата в формате YYYY-MM-DD, значение - массив бронирований
   */
  get bookingsByDate(): Map<string, AdminBookingResponseDto[]> {
    const grouped = new Map<string, AdminBookingResponseDto[]>();

    this.bookings.forEach((booking) => {
      const date = format(parseISO(booking.start_time), 'yyyy-MM-dd');
      
      if (!grouped.has(date)) {
        grouped.set(date, []);
      }
      
      grouped.get(date)!.push(booking);
    });

    return grouped;
  }

  /**
   * Геттер: группировка бронирований по временным слотам
   * Возвращает Map, где ключ - временной слот в формате HH:mm, значение - массив бронирований
   */
  get bookingsByTimeSlot(): Map<string, AdminBookingResponseDto[]> {
    const grouped = new Map<string, AdminBookingResponseDto[]>();

    this.bookings.forEach((booking) => {
      const timeSlot = format(parseISO(booking.start_time), 'HH:mm');
      
      if (!grouped.has(timeSlot)) {
        grouped.set(timeSlot, []);
      }
      
      grouped.get(timeSlot)!.push(booking);
    });

    return grouped;
  }

  /**
   * Геттер: получить бронирования для конкретной даты
   */
  getBookingsForDate(date: Date): AdminBookingResponseDto[] {
    const dateKey = format(date, 'yyyy-MM-dd');
    return this.bookingsByDate.get(dateKey) || [];
  }

  /**
   * Геттер: получить бронирования для конкретного временного слота в конкретную дату
   */
  getBookingsForTimeSlot(date: Date, timeSlot: string): AdminBookingResponseDto[] {
    const dateKey = format(date, 'yyyy-MM-dd');
    const bookingsForDate = this.bookingsByDate.get(dateKey) || [];
    
    return bookingsForDate.filter((booking) => {
      const bookingTimeSlot = format(parseISO(booking.start_time), 'HH:mm');
      return bookingTimeSlot === timeSlot;
    });
  }

  /**
   * Геттер: статистика по статусам
   */
  get statusStats() {
    const stats = {
      pending_confirmation: 0,
      confirmed: 0,
      completed: 0,
      cancelled: 0,
    };

    this.bookings.forEach((booking) => {
      const status = booking.status as BookingStatus;
      if (status in stats) {
        stats[status]++;
      }
    });

    return stats;
  }

  /**
   * Геттер: общая выручка за выбранный период
   */
  get totalRevenue(): number {
    return this.bookings
      .filter((b) => b.status === 'completed' || b.status === 'confirmed')
      .reduce((sum, booking) => sum + booking.total_cost, 0);
  }

  /**
   * Полная очистка store
   * Используется при logout или смене пользователя
   */
  reset(): void {
    this.bookings = [];
    this.pendingBookings = [];
    this.selectedBooking = null;
    this.bookingsCache.clear();
    this.clientDisplayNames.clear();
    this.isLoading = false;
    this.isLoadingDetails = false;
    this.isLoadingPending = false;
    this.error = null;
    this.isInitialLoad = true;
    this.fetchRequestId = 0;
    this.total = 0;
    this.offset = 0;
  }
}

// Экспортируем singleton instance
export const bookingsStore = new BookingsStore();
